import React from 'react';
import { Box, HStack, Text, VStack, Badge } from '@chakra-ui/react';
import { X, ArrowRight } from 'lucide-react';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import { ISession } from '../../../../models/sessions';
import { IReassignmentHistoryEntry } from '../../../../models/m6-2';
import CustomButton from '../../../../shared/CustomButton/CustomButton';

dayjs.locale('it');

interface ReassignmentHistoryModalProps {
  session: ISession & { reassignmentHistory?: IReassignmentHistoryEntry[] };
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Modale storico riassegnazioni team per una sessione (M6.2).
 * Mostra la lista cronologica degli eventi di riassegnazione con:
 * data, from → to team, utente che ha effettuato, motivazione opzionale.
 */
export const ReassignmentHistoryModal: React.FC<ReassignmentHistoryModalProps> = ({
  session,
  isOpen,
  onClose,
}) => {
  if (!isOpen) return null;

  const history: IReassignmentHistoryEntry[] = session.reassignmentHistory ?? [];

  return (
    <Box
      position="fixed"
      top={0}
      left={0}
      right={0}
      bottom={0}
      bg="blackAlpha.500"
      zIndex={1100}
      display="flex"
      alignItems="center"
      justifyContent="center"
      onClick={onClose}
    >
      <Box
        bg="var(--c-card-bg)"
        borderRadius="lg"
        p={6}
        maxWidth="560px"
        width="92%"
        maxH="80vh"
        overflowY="auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <HStack justifyContent="space-between" alignItems="flex-start" mb={4}>
          <Box>
            <Text fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="0.05em">
              Sessione
            </Text>
            <Text fontSize="xl" fontWeight="700">
              Storico riassegnazioni team
            </Text>
            {session.name && (
              <Text fontSize="sm" color="gray.600">
                {session.name}
              </Text>
            )}
          </Box>
          <CustomButton color="text" onClick={onClose}>
            <X size={16} />
          </CustomButton>
        </HStack>

        {/* Empty state */}
        {history.length === 0 ? (
          <Box bg="gray.50" p={6} borderRadius="md" textAlign="center">
            <Text color="gray.400" fontSize="sm">
              Nessuna riassegnazione registrata per questa sessione.
            </Text>
          </Box>
        ) : (
          <VStack alignItems="stretch" gap={3}>
            {[...history]
              .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime())
              .map((entry, idx) => (
                <Box
                  key={idx}
                  p={3}
                  bg="gray.50"
                  borderRadius="md"
                  border="1px solid"
                  borderColor="gray.200"
                >
                  <HStack justifyContent="space-between" mb={2}>
                    <Text fontSize="xs" color="gray.500" fontWeight="medium">
                      {dayjs(entry.at).format('D MMM YYYY · HH:mm')}
                    </Text>
                    <Badge colorPalette="blue" size="sm">
                      #{history.length - idx}
                    </Badge>
                  </HStack>

                  {/* from → to */}
                  <HStack gap={2} flexWrap="wrap" mb={2}>
                    <Box
                      px={2}
                      py={1}
                      bg="red.50"
                      borderRadius="sm"
                      border="1px solid"
                      borderColor="red.200"
                    >
                      <Text fontSize="xs" color="red.700" fontWeight="medium">
                        {String(entry.fromTeamId)}
                      </Text>
                    </Box>
                    <ArrowRight size={14} color="#718096" />
                    <Box
                      px={2}
                      py={1}
                      bg="green.50"
                      borderRadius="sm"
                      border="1px solid"
                      borderColor="green.200"
                    >
                      <Text fontSize="xs" color="green.700" fontWeight="medium">
                        {String(entry.toTeamId)}
                      </Text>
                    </Box>
                  </HStack>

                  {/* Utente */}
                  <Text fontSize="xs" color="gray.500">
                    Effettuato da: <strong>{String(entry.by)}</strong>
                  </Text>

                  {/* Motivazione opzionale */}
                  {entry.reason && (
                    <Box mt={2} p={2} bg="yellow.50" borderRadius="sm" border="1px solid" borderColor="yellow.100">
                      <Text fontSize="xs" color="yellow.800">
                        <strong>Motivazione:</strong> {entry.reason}
                      </Text>
                    </Box>
                  )}
                </Box>
              ))}
          </VStack>
        )}

        {/* Footer */}
        <HStack justifyContent="flex-end" mt={4}>
          <CustomButton color="text" onClick={onClose}>
            Chiudi
          </CustomButton>
        </HStack>
      </Box>
    </Box>
  );
};
